import {
  GranteeResponse,
  Meldung,
  FilterItem,
  MeldungDetail,
  Result,
} from "@/types";
import RequestService from "./RequestService";
import Router from "@/router";
import AlertService from "./AlertService";
import StorageService from "./StorageService";
import { AxiosError } from "axios";
import { FilterParameter } from "@/types/FilterParameter";

/** endpoint definitions */
const endpoint = {
  grantee: {
    login: () => "grantee/login",
    logout: () => "grantee/logout",
  },
  meldestelle: {
    eingang: (meldestellenId?: number) =>
      `meldestelle/${meldestellenId || ""}/eingang`,
    meldestellen: (meldestellenId?: number) =>
      `meldestelle/${meldestellenId || ""}/meldestellen`,
    meldungen: (meldestellenId?: number) =>
      `meldestelle/${meldestellenId || ""}/meldungen`,
    meldungstypen: (meldestellenId?: number) =>
      `meldestelle/${meldestellenId || ""}/meldungstypen`,
  },
  meldung: {
    display: (meldungsId?: number) => `meldung/${meldungsId || ""}/display`,
    bestaetigen: (meldungsId?: number) =>
      `meldung/${meldungsId || ""}/bestaetigen`,
    delete: (meldungsId?: number) => `meldung/${meldungsId || ""}/delete`,
    gelesen: (meldungsId?: number) => `meldung/${meldungsId || ""}/gelesen`,
    pdf: (meldungsId?: number) => `meldung/${meldungsId || ""}/pdf`,
  },
};

class ApiService {
  private baseURL = process.env.VUE_APP_BASE_URL;
  private requestService: RequestService;
  private user: GranteeResponse | undefined;
  private lastRoute: string | undefined;

  constructor() {
    // initliasie RequestService
    const header = { "Access-Control-Allow-Origin": "*" };
    this.requestService = new RequestService(this.baseURL, undefined, header);
    // set custom error handler
    this.requestService.setErrorHandler(this.errorHandler);
    // load last loggedin User
    this.setUser(StorageService.get("user"));
  }

  /***************************************
   * Account API
   **************************************/

  /**
   * sets user autorization
   * @param user user
   */
  protected setUser(user: GranteeResponse | undefined = undefined): void {
    // set new user
    this.user = user;
    // update authorization header for requests
    this.requestService.addHeaders({ authorization: user?.JSESSIONID });
    // update user in storage
    StorageService.set("user", user);
  }

  /**
   * Get the meldestelleId from the user object
   */
  protected getMeldestellenId(): number {
    return this.user?.meldestelle || 0;
  }

  public isLoggedIn(): boolean {
    return this.user?.JSESSIONID !== undefined;
  }

  /**
   * logs in and sets user for furhter requests
   * @param username user name
   * @param password user password
   * @returns promise
   */
  public login(username: string, password: string): Promise<void> {
    const params = { user: username, key: password };

    // do login request
    return this.requestService
      .get<GranteeResponse>(endpoint.grantee.login(), params)
      .then((res: GranteeResponse) => {
        console.debug(res.result);
        if (res.result === "ok") {
          // set user id
          this.setUser(res);
          // show success message
          AlertService.success("Angemeldet");
          // rediect to last page
          this.goToLastRoute();
        } else {
          throw new Error(res.result || "Anmeldung Fehlgeschlagen");
        }
      });
  }

  /**
   * logsout and removes user id
   * @returns promise
   */
  public logout(): Promise<void> {
    return this.requestService
      .get<GranteeResponse>(endpoint.grantee.logout())
      .then((res: GranteeResponse) => {
        console.debug(res.result);
        // remove user id
        this.setUser();
        // show logout message
        AlertService.info("Ausgeloggt");
        // go to login page
        this.goToLogin();
      });
  }

  /***************************************
   * Meldestellen API
   **************************************/

  /**
   * Returns a list of meldungs typs
   * @returns promise
   */
  public getMeldungsTypen(): Promise<FilterItem[]> {
    // check login status
    this.checkAuth();
    // do request
    return this.requestService.get<FilterItem[]>(
      endpoint.meldestelle.meldungstypen(this.getMeldestellenId())
    );
  }

  /**
   * Returns a list of meldestellen
   * @returns promise
   */
  public getMeldestellen(): Promise<FilterItem[]> {
    // check login status
    this.checkAuth();
    // do request
    return this.requestService.get<FilterItem[]>(
      endpoint.meldestelle.meldestellen(this.getMeldestellenId())
    );
  }

  /**
   * return meldeliste entries
   * @param page page number
   * @param filter filter parameter
   * @returns Promise
   */
  public getMeldungen(
    page: number,
    filter?: FilterParameter
  ): Promise<Meldung[]> {
    const params = {
      "sort-order": "desc",
      "page-size": process.env.VUE_APP_PAGING_SIZE || 10,
      page: page,
      "filter-item": filter?.query || "",
      "filter-type": filter?.type || "",
      "filter-absender": filter?.sender || "",
    };

    // check login status
    this.checkAuth();
    // do request
    return this.requestService.get<Meldung[]>(
      endpoint.meldestelle.meldungen(this.getMeldestellenId()),
      params
    );
  }

  /**
   * @deprecated
   * returns the eingangsliste entries
   * @param search search parameter
   * @returns promise
   */

  public getEingangsliste(search?: string): Promise<Meldung[]> {
    const params = { "filter-item": search || "" };

    // check login status
    this.checkAuth();
    // do request
    return this.requestService.get<Meldung[]>(
      endpoint.meldestelle.eingang(this.user?.meldestelle),
      params
    );
  }

  /************************
   * Meldung API
   ***********************/

  /**
   * Returns a detailed object of a meldung
   * @param id meldung id
   * @returns Promise
   */
  public getMeldung(id: number): Promise<MeldungDetail> {
    // check login status
    this.checkAuth();
    // do request
    return this.requestService.get<MeldungDetail>(endpoint.meldung.display(id));
  }

  /**
   * Confirm a meldung
   * @param id meldungid
   * @returns Promise
   */
  public confirmMeldung(id: number): Promise<Result> {
    // check login status
    this.checkAuth();
    // do request
    return this.requestService.get<Result>(endpoint.meldung.bestaetigen(id));
  }

  /**
   * Set a meldung as read
   * @param id meldungid
   * @returns Promise
   */
  public readMeldung(id: number): Promise<Result> {
    // check login status
    this.checkAuth();
    // do request
    return this.requestService.get<Result>(endpoint.meldung.gelesen(id));
  }

  /***************************************
   * Protected Functions
   **************************************/

  /**
   * checks if the user is logged in, if not it throws an error and redirects to login page
   */
  protected checkAuth(): void {
    if (!this.isLoggedIn()) {
      console.debug("No user authenticated");
      // save last route for redirect
      this.lastRoute = Router.currentRoute.path;
      // change route to login page
      this.goToLogin();
      // throw not authorised error
      throw new Error("Bitte zuerst anmelden!");
    }
  }

  /**
   * Validate the repsonse for not different status codes
   * @param error AxiosError
   * @returns response data or throws error
   */
  private errorHandler(error: AxiosError) {
    let errorMessage;

    switch (error.response?.status) {
      case 400: //wrong input
        break;

      case 401:
        // unauthorized
        errorMessage = "Unautorized";
        // show error message
        AlertService.error("Autorisierungs Fehler");
        console.error(errorMessage, error);
        // user is not valid anymore -> delete user
        apiService.setUser();
        // redirect to login page
        apiService.goToLogin();
        throw new Error(errorMessage);

      default:
        // unexpected response
        errorMessage = "Verbinungsprobleme zum Server";
        console.error(errorMessage, error);
        // show error message
        AlertService.error("Verbinungsprobleme zum Server");
        break;
    }

    // check if server responsed same data
    if (error.response?.data) return error.response.data;
    // else throw error message
    else throw new Error(errorMessage);
  }

  /**
   * Change route to login page
   */
  protected goToLogin(): void {
    if (Router.currentRoute.name != "Login") {
      console.debug("redirect to login");
      Router.push({ name: "Login" });
    }
  }

  /**
   * changes route to last route befor login or to home page
   */
  protected goToLastRoute(): void {
    console.debug("redirect to last route");
    // redirect to last route if set
    if (this.lastRoute) Router.push({ path: this.lastRoute });
    // else redirect to home
    else Router.push({ name: "Home" });
  }
}

// create singleton
const apiService = new ApiService();
export default apiService;
